import { useContext, useEffect, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import PatagoniaSidebarHeader from 'ContentEditorUI/components/sidebar/PatagoniaSidebarHeader';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import Immutable from 'immutable';
import { ArtifactsWithNoGatherer } from 'xray-recommendation-lib/constants';
import { useGenerateAuditResults } from 'xray-recommendation-lib/utils/auditResultsUtils';
import { getInpageGathererArtifacts } from 'ContentEditorUI/redux/selectors/optimizationSelectors';
import { useTracker } from 'ContentEditorUI/utils/UsageTracker';
import XRayPatagoniaRecommendationContainer from '../XRayPatagoniaRecommendationContainer';
import { getAuth } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getContentModelHtmlTitle, getContentModelId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { ErrorBoundary } from 'xray-recommendation-lib/js/internal/ErrorBoundary';
import { GathererConfig, GathererExecutionLocations, GathererKeys } from 'xray-recommendation-lib/gatherers/allGatherers';
import { getHasUnsavedChanges } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getActiveApexDomain } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getGathererKeys } from '../../../utils/xrayUtils';
import { getAllNormalizedFormModulesInsideLayout, getModules, getFakePostBodyHTML } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { useCurrentContentContext } from '../../../utils/contentContextUtils';
import { getIsPage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getModuleIdsByBuiltinType } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import memoizeOne from 'react-utils/memoizeOne';
import { View } from 'foundations-components/components/View';
// @ts-expect-error Untyped
import EditorActionsAndEventsContext from 'ContentEditorUI/context/EditorActionsAndEventsContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const convertModulesToImmutable = memoizeOne(val => {
  return Immutable.Map(val);
});
const useParentEditorStateSelectors = () => {
  const store = useStore();
  const parentEditorStateSelectors = useMemo(() => {
    const selectContentModelHtmlTitle = () => {
      return getContentModelHtmlTitle(store.getState());
    };
    const selectPostBody = () => {
      return getFakePostBodyHTML(store.getState());
    };
    const selectApexDomain = () => {
      return getActiveApexDomain(store.getState());
    };
    const selectAllFormsInsideLayoutSection = () => {
      return getAllNormalizedFormModulesInsideLayout(store.getState());
    };
    const selectAllModules = () => {
      return convertModulesToImmutable(getModules(store.getState()));
    };
    const selectModuleIdsByBuiltInType = () => {
      return getModuleIdsByBuiltinType(store.getState());
    };
    return {
      selectAllFormsInsideLayoutSection,
      selectApexDomain,
      selectContentModelHtmlTitle,
      selectPostBody,
      selectAllModules,
      selectModuleIdsByBuiltInType
    };
  }, [store]);
  return parentEditorStateSelectors;
};
const useGetAllArtifacts = config => {
  const inpageEditorArtifacts = useSelector(getInpageGathererArtifacts);
  const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
  const editorStateSelectors = useParentEditorStateSelectors();
  const gathererKeys = useMemo(() => {
    return getGathererKeys(config);
  }, [config]);
  const isPage = useSelector(getIsPage);
  const parentEditorArtifacts = useMemo(() => {
    return gathererKeys.reduce((newArtifacts, key) => {
      const gatherer = GathererConfig[key];
      if (gatherer.location === GathererExecutionLocations.PARENT_EDITOR) {
        const artifact = gatherer.execute({
          editorStateSelectors
        });
        newArtifacts[key] = artifact;
      }
      return newArtifacts;
    }, {});
    // We only want to re-compute when hasUnsavedChanges goes from true -> false.
    // This will also re-compute when going from false -> true, but the computation
    // is not expensive because it's calling selectors from a memoized store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, editorStateSelectors, hasUnsavedChanges]);

  // If user is using a custom template, they potentially don't have the default post_body module
  // Based on the KB article: https://knowledge.hubspot.com/seo/understand-seo-recommendations#:~:text=Please%20note%3A%20the,coded%20blog%20post%20template
  // We historically checked for the blog-post-wrapper class in our backend optimization service
  // Before Xray was switched to a frontend-only implementation
  const isCustomTemplate = parentEditorArtifacts.POST_BODY === null;
  let targetSelector = isCustomTemplate ? '.blog-post-wrapper' : '#hs_cos_wrapper_post_body';
  if (isPage) {
    targetSelector = 'body';
  }
  const {
    currentContext: fullPostBodyText,
    getCurrentContentContext
  } = useCurrentContentContext(targetSelector);
  const {
    bus
  } = useContext(EditorActionsAndEventsContext);
  useEffect(() => {
    // We only want to get post body if we are in blog editor
    if (gathererKeys.includes(GathererKeys.POST_BODY) && bus !== null && bus !== void 0 && bus.ready) {
      getCurrentContentContext();
    }

    // We only want to call getCurrentContentContext on initial render,
    // and after postBody has been edited and on bus.ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentEditorArtifacts.POST_BODY, bus === null || bus === void 0 ? void 0 : bus.ready]);
  return Object.assign({}, inpageEditorArtifacts, parentEditorArtifacts, {
    [ArtifactsWithNoGatherer.POST_BODY_NO_MARKUP]: fullPostBodyText
  });
};
const PatagoniaXrayPanel = ({
  config
}) => {
  const tracker = useTracker();
  useEffect(() => {
    if (tracker) {
      tracker.track('xrayPageView', {
        screen: 'xray panel'
      });
    }
  }, [tracker]);
  const auth = useSelector(getAuth);
  const contentId = useSelector(getContentModelId);
  const artifacts = useGetAllArtifacts(config);
  const auditResults = useGenerateAuditResults({
    config,
    artifacts
  });
  if (!tracker) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PatagoniaSidebarHeader, {
      headerText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "patagonia.sidebar.toolsMenu.optimizer"
      })
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "row",
      "data-test-id": "patagonia-sidebar-xray-panel",
      blockSize: "100%",
      children: /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        blockSize: "100%",
        paddingBlockEnd: "1200",
        children: /*#__PURE__*/_jsx(XRayPatagoniaRecommendationContainer, {
          artifacts: artifacts,
          config: config,
          auditResults: auditResults,
          auth: auth,
          contentId: contentId,
          tracker: tracker
        })
      })
    })]
  });
};
const PatagoniaXrayPanelWrapper = ({
  config
}) => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    children: /*#__PURE__*/_jsx(PatagoniaXrayPanel, {
      config: config
    })
  });
};
export default PatagoniaXrayPanelWrapper;